import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import { formatPhone } from '../../lib/phone';
import {
    FIELD_LABELS,
    FIELD_ORDER,
    TILE_COUNTDOWN_SECONDS,
    completionCounts,
} from '../../lib/reservationFromConversation';

// BL-177 Step 2k: the review tile for "Create reservation". (Step 2j: this is the locked BASE for the platform popup, now the shared Modal3D.)
// A medium raised 3D card (Modal3D, .ia-popup3d family, src/styles/modal3d.css + step2k.css) showing the
// reservation read out of the conversation, problem fields highlighted (orange border + the words "Missing" / "Check this"),
// with Cancel / Edit / Approve (bottom-right) and a visible 5-second ring countdown. Any interaction with the tile (press, key, focus, touch) stops the
// countdown for good; nothing is added until Approve. If the countdown runs out untouched, onTimeout fires once and the caller
// adds the trip to the dispatch list as "Needs info". The same tile completes a Needs info trip later (countdown={false}).

const TICK_MS = 200;
const RING_LENGTH = 2 * Math.PI * 15.5;
const WIDE_FIELDS = new Set(['pickup_location', 'dropoff_location', 'notes']);
const OPTIONAL_FIELDS = new Set(['flight_info', 'vehicle', 'notes']);
const INPUT_TYPES = { date: 'date', time: 'time', passenger_count: 'number', luggage_count: 'number', email: 'email', phone: 'tel' };

function fieldDisplay(key, value) {
    if (value === null || value === undefined || value === '') return '—';
    if (key === 'phone') return formatPhone(String(value));
    if (key === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
        const [year, month, day] = String(value).split('-').map(Number);
        return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Date.UTC(year, month - 1, day)));
    }
    if (key === 'time' && /^\d{2}:\d{2}$/.test(String(value))) {
        const [hours, minutes] = String(value).split(':').map(Number);
        return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' }).format(new Date(Date.UTC(2000, 0, 1, hours, minutes)));
    }
    return String(value);
}

export default function ReservationTile({
    reservation,
    countdown = true,
    seconds = TILE_COUNTDOWN_SECONDS,
    startInEdit = false,
    busy = false,
    error = '',
    title = 'Review reservation',
    subtitle = '',
    approveLabel = 'Approve',
    onApprove,
    onCancel,
    onTimeout,
}) {
    const [fields, setFields] = useState(() => ({ ...(reservation?.fields || {}) }));
    const [editing, setEditing] = useState(startInEdit);
    const [stopped, setStopped] = useState(!countdown);
    const [remainingMs, setRemainingMs] = useState(seconds * 1000);
    const deadline = useRef(Date.now() + seconds * 1000);
    const fired = useRef(false);

    const problems = useMemo(() => (Array.isArray(reservation?.problems) ? reservation.problems : []), [reservation?.problems]);
    const problemByField = useMemo(() => Object.fromEntries(problems.map((p) => [p.field, p])), [problems]);
    const counts = completionCounts(fields, problems, reservation?.onFileFields || []);

    // a fresh answer from the server (Approve with something still wrong) replaces the values and the highlights
    useEffect(() => { setFields({ ...(reservation?.fields || {}) }); }, [reservation?.fields]);

    const stopCountdown = useCallback(() => { setStopped(true); }, []);

    useEffect(() => {
        if (stopped) return undefined;
        const timer = setInterval(() => {
            const left = Math.max(0, deadline.current - Date.now());
            setRemainingMs(left);
            if (left <= 0 && !fired.current) {
                fired.current = true;
                clearInterval(timer);
                onTimeout?.();
            }
        }, TICK_MS);
        return () => clearInterval(timer);
    }, [stopped, onTimeout]);

    const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
    const keys = FIELD_ORDER.filter((key) => !OPTIONAL_FIELDS.has(key) || editing || problemByField[key] || (fields[key] !== null && fields[key] !== undefined && fields[key] !== ''));

    const setField = (key, value) => setFields((current) => ({ ...current, [key]: value }));
    const approve = () => onApprove?.(fields);

    const ringFraction = stopped ? 0 : Math.max(0, Math.min(1, remainingMs / (seconds * 1000)));
    const subtitleText = subtitle || (problems.length
        ? `${counts.good} of ${counts.total} required details are complete. ${problems.length === 1 ? 'One detail needs' : `${problems.length} details need`} a look.`
        : 'Everything required is here.');

    return (
        <Modal3D
            title={title}
            subtitle={subtitleText}
            stripe={problems.length ? 'orange' : 'peacock'}
            testId="reservation-tile"
            aria-label={title}
            data-countdown={stopped ? 'stopped' : 'running'}
            onClose={() => onCancel?.()}
            onInteract={stopCountdown}
            actions={(
                <>
                    <Button3D onClick={() => onCancel?.()} disabled={busy}>Cancel</Button3D>
                    <Button3D onClick={() => setEditing((current) => !current)} aria-pressed={editing} disabled={busy}>{editing ? 'Editing' : 'Edit'}</Button3D>
                    <Button3D variant="go" onClick={approve} disabled={busy}>{busy ? 'Working…' : approveLabel}</Button3D>
                </>
            )}
        >
            <ul className="ia-resv-grid" aria-label="Reservation details">
                {keys.map((key) => {
                    const problem = problemByField[key];
                    const label = FIELD_LABELS[key];
                    const inputId = `ia-resv-${key}`;
                    return (
                        <li
                            key={key}
                            className={`ia-resv-field${WIDE_FIELDS.has(key) ? ' ia-resv-field--wide' : ''}`}
                            data-field={key}
                            data-problem={problem ? problem.kind : undefined}
                        >
                            <div className="ia-resv-field__label">
                                <label htmlFor={editing ? inputId : undefined}>{label}</label>
                                {problem && <span className="ia-resv-chip">{problem.kind === 'missing' ? 'Missing' : 'Check this'}</span>}
                            </div>
                            {editing ? (
                                key === 'notes' ? (
                                    <textarea id={inputId} rows={2} value={fields[key] ?? ''} onChange={(event) => setField(key, event.target.value)} />
                                ) : (
                                    <input
                                        id={inputId}
                                        type={INPUT_TYPES[key] || 'text'}
                                        min={key === 'passenger_count' ? 1 : key === 'luggage_count' ? 0 : undefined}
                                        value={fields[key] ?? ''}
                                        onChange={(event) => setField(key, event.target.value)}
                                    />
                                )
                            ) : (
                                <div className="ia-resv-field__value">{fieldDisplay(key, fields[key])}</div>
                            )}
                            {problem && <p className="ia-resv-field__why">{problem.message}</p>}
                        </li>
                    );
                })}
            </ul>

            {countdown && (
                <div className={`ia-resv-countdown${stopped ? ' ia-resv-countdown--stopped' : ''}`} data-testid="reservation-countdown">
                    <svg className="ia-resv-ring" viewBox="0 0 36 36" width="44" height="44" aria-hidden="true" data-ring-fraction={ringFraction.toFixed(2)}>
                        <circle className="ia-resv-ring__track" cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" />
                        <circle
                            className="ia-resv-ring__fill" cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" strokeLinecap="round"
                            strokeDasharray={RING_LENGTH} strokeDashoffset={RING_LENGTH * (1 - ringFraction)} transform="rotate(-90 18 18)"
                        />
                        {!stopped && <text x="18" y="22.5" textAnchor="middle" className="ia-resv-ring__num">{secondsLeft}</text>}
                    </svg>
                    <p className="ia-resv-countdown__text" role="timer" aria-live="off">
                        {stopped
                            ? 'Countdown stopped. Nothing is added to dispatch until you press Approve.'
                            : `No response in ${secondsLeft} s adds this to dispatch as Needs info.`}
                    </p>
                </div>
            )}
            {error && <p className="ia-resv-error" role="alert">{error}</p>}
        </Modal3D>
    );
}
